import { useRef, useState, useEffect } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import app, { auth } from "./hooks/firebase";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import {Card, Container, Row, Col} from "react-bootstrap";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import cn from "classnames";
import Settings from "./components/settings";
import About from "./components/about";
import Faqs from "./components/faq";
import NotFound from "./components/notfound";
import TextInput from "./components/textinput";
import Login from "./components/loginHandler";
import UserNavigation from "./components/userNavigation";
import Updates from "./components/updates";
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string") {
throw new Error("Set REACT_APP_GEMINI_API_KEY in your .env file");
}
const host = "generativelanguage.googleapis.com";
const uri = `wss://${host}/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent`;
function MainApp({
videoRef,
videoStream,
setVideoStream,
user,
}: {
videoRef: React.RefObject<HTMLVideoElement>;
videoStream: MediaStream | null;
setVideoStream: (stream: MediaStream | null) => void;
user: User | null;
}) {
useEffect(() => {
document.title = "LensTalk:  An intelligent virtual assistant that brings conversations to life through video";
}, []);
return (
<Container>
<div className="main-app-area">
<Card>
<Card.Body>
<Card.Title>
<h1>LensTalk</h1>
</Card.Title>
<p>A wide-scope assistant with sharp vision and crisp voice</p>
<p>for any issues, suggestions, feedbacks, comments, or requests, <a href="https://www.techassistantforblind.com/contact">Contact us</a></p>
</Card.Body>
</Card>
{user ? (
<Container>
<Row>
<Col>
<Altair />
</Col>
</Row>
<Row>
<Col>
<video
className={cn("stream", { hidden: !videoRef.current || !videoStream })}
ref={videoRef}
autoPlay
playsInline
/>
</Col>
</Row>
<Row>
<Col>
<ControlTray
videoRef={videoRef}
supportsVideo={true}
onVideoStreamChange={setVideoStream}
>
<TextInput />
</ControlTray>
</Col>
</Row>
</Container>
) : (
<Card>
<Card.Body>
<Card.Title>
<h2>Please sign in</h2>
</Card.Title>
<p>You need to sign in to use this model.</p>
<Link to="/login">Sign in</Link>
</Card.Body>
</Card>
)}
</div>
</Container>
);
}
function App() {
const navigate = useNavigate();
const videoRef = useRef<HTMLVideoElement>(null);
const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
const [user, setUser] = useState<User | null>(null);
const [isMenuOpen, setIsMenuOpen] = useState(false);
useEffect(() => {
const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
if (currentUser) {
setUser(currentUser);
} else {
setUser(null);
navigate("/login"); // Redirect to Login if not authenticated
}
});
return () => unsubscribe();
}, [navigate]);
return (
<Container>
<div className="App">
<LiveAPIProvider url={uri} apiKey={API_KEY}>
<div className="streaming-console">
{/* Header */}
<Updates />
<header>
<a href="https://www.techassistantforblind.com">Tech Assistant for Blind</a>
<a href="https://www.techassistantforblind.com/ai">Tech Assistant AI</a>
<button className="btn btn-primary" aria-expanded={isMenuOpen} onClick={() => setIsMenuOpen(!isMenuOpen)}>{!isMenuOpen ? 'Open Menu' : 'Close Menu'}</button>
{isMenuOpen && (
<nav aria-label="Global">
<Row>
<Col><Link to="/">Home</Link></Col>
<Col><Link to="/about">About the project</Link></Col>
<Col><Link to="/faq">FAQ's</Link></Col>
<Col><Link to="/settings">Settings</Link></Col>
</Row>
</nav>
)}
<Row>
<Col>
<UserNavigation />
</Col>
</Row>
</header>
<main>
<Routes>
<Route path="/login" element={<Login />} />
<Route
path="/"
element={
<MainApp
videoRef={videoRef}
videoStream={videoStream}
setVideoStream={setVideoStream}
user={user}
/>
}
/>
<Route path="/settings" element={<Settings />} />
<Route path="/about" element={<About />} />
<Route path="/faq" element={<Faqs />} />
<Route path="*" element={<NotFound />} />
</Routes>
</main>
<footer>
<ul style={{ listStyleType: "none" }}>
<li>
<Link to="/">Home</Link>
</li>
<li>
<Link to="/faq">FAQ's</Link>
</li>
<li>
<a href="https://www.techassistantforblind.com/events">our latest happenings</a>
</li>
<li>
<a href="https://www.techassistantforblind.com/blog">Blog</a>
</li>
<li>
<a href="https://www.techassistantforblind.com/contact">Contact us</a>
</li>
<li>
<a href="https://www.techassistantforblind.com/privacy-policy">Privacy Policy</a>
</li>
<li>
<a href="https://www.techassistantforblind.com/terms">Terms and conditions</a>
</li>
<li>
<a href="https://www.techassistantforblind.com/ai-usage-policy">AI Usage and regulatory guydeline</a>
</li>
</ul>
<div>
<p>
&copy; {new Date().getFullYear()} Copyright Tech Assistant for Blind
</p>
<p className="col-6">
LensTalk 💙 | Powered by{" "}
<a href="https://www.techassistantforblind.com/ai" target="_blank" rel="noreferrer">
Tech Assistant AI Brainstorm
</a>
</p>
</div>
</footer>
</div>
</LiveAPIProvider>
</div>
</Container>
);
}

export default App;
